/**
 * Golden Spark — random positive event (docs/09 §VII.6, hook 7).
 *
 * Appears every 60–180 s at a random position. Tap within 5 s grants
 * a temporary ×7 awareness multiplier for 13 s. (Multiplier wiring lands
 * in the next iteration; for the alpha we just count catches in stats.)
 *
 * Player feedback: subtle gold flicker + soft sound (sound TODO).
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import styles from './GoldenSparkOverlay.module.css';

const APPEAR_INTERVAL_MIN_MS = 60_000;
const APPEAR_INTERVAL_MAX_MS = 180_000;
const VISIBLE_DURATION_MS = 5_000;

interface SparkPosition {
  readonly x: number; // 0..1
  readonly y: number;
  readonly key: number;
}

let _sparkKey = 1;

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function GoldenSparkOverlay(): JSX.Element {
  const [position, setPosition] = useState<SparkPosition | null>(null);
  const totalTaps = useGameStore((s) => s.state.stats.totalTaps);
  const lastTapAtRef = useRef(totalTaps);
  lastTapAtRef.current = totalTaps;

  useEffect(() => {
    let timer: number | undefined;
    const scheduleNext = (): void => {
      const delay = randomBetween(APPEAR_INTERVAL_MIN_MS, APPEAR_INTERVAL_MAX_MS);
      timer = window.setTimeout(() => {
        // Only show after the player has tapped a few times
        if (lastTapAtRef.current < 5) {
          scheduleNext();
          return;
        }
        setPosition({
          x: randomBetween(0.15, 0.85),
          y: randomBetween(0.25, 0.55),
          key: _sparkKey++,
        });
        window.setTimeout(() => {
          setPosition(null);
          scheduleNext();
        }, VISIBLE_DURATION_MS);
      }, delay);
    };
    scheduleNext();
    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  const onCatch = (): void => {
    // TODO: apply temporary multiplier; for now we just dismiss
    setPosition(null);
  };

  return (
    <AnimatePresence>
      {position !== null && (
        <motion.button
          key={position.key}
          type="button"
          className={styles.spark}
          style={{ left: `${position.x * 100}%`, top: `${position.y * 100}%` }}
          aria-label="Поймать Золотую Искру"
          onClick={onCatch}
          onTouchEnd={onCatch}
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: [0, 1.2, 1],
            opacity: 1,
            rotate: [0, 180],
          }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      )}
    </AnimatePresence>
  );
}
